import type {SWRMutationConfiguration} from 'swr/mutation';
import useSWRMutation from 'swr/mutation';

import {baseLogger} from '../helpers';

import type {BaseClient, CacheKey, ExtractEndpointRequestType, ExtractEndpointResponseType} from './types';
import {isLegacyCacheKey, isValidCacheKey} from './types';

/** @description React hook that accepts client generated by openapi-generator typescript-fetch */
const useSWRMutateApi = <
  TClient extends BaseClient,
  TKey extends CacheKey<TEntity, TClient>,
  TRequest extends ExtractEndpointRequestType<TClient, TKey[`endpoint`]>,
  TResponse extends ExtractEndpointResponseType<TClient, TKey[`endpoint`]>,
  // TODO: infer error type from client
  TConfig extends SWRMutationConfiguration<TResponse, TRequest> = SWRMutationConfiguration<TResponse, TRequest>,
  TEntity extends object = object,
>(
  client: TClient,
  key: TKey,
  config: TConfig = {} as TConfig,
) => {
  const fetcher = (cacheKey: TKey, {arg}: {arg: TRequest}) => {
    if (!isValidCacheKey(cacheKey) && !isLegacyCacheKey(cacheKey, client)) {
      console.warn(`Invalid cache key`, cacheKey);
      return null;
    }

    baseLogger.debug(`Fetching data for cache key`, cacheKey);

    const endpoint = typeof cacheKey === `string` ? cacheKey : cacheKey.endpoint;

    if (!(endpoint in client) || typeof client[endpoint] !== `function`) {
      return Promise.reject(new Error(`Invalid endpoint: ${String(endpoint)}`));
    }
    // We need to bind `this` as the apiClient, and pass the
    // request as the only argument
    return (client[endpoint] as Function).apply(client, [arg ?? {}]);
  };

  return useSWRMutation(key, fetcher as any, config);
};

export default useSWRMutateApi;
